<template>
  <div class="bank-details" :class="{ 'bank-details--bordered': bordered }">
    <h5 v-if="showTitle" class="bank-title">
      <slot name="title">Bank Details</slot>
    </h5>

    <div class="bank-grid" :class="gridClass">
      <div class="bank-item">
        <span class="bank-label">Account Name</span>
        <span class="bank-value">{{ accountName }}</span>
      </div>
      <div class="bank-item">
        <span class="bank-label">Bank</span>
        <span class="bank-value">{{ bankName }}</span>
      </div>
      <div class="bank-item">
        <span class="bank-label">Account Type</span>
        <span class="bank-value">{{ accountType }}</span>
      </div>
      <div class="bank-item">
        <span class="bank-label">Account Number</span>
        <span class="bank-value bank-value--highlight">{{ accountNumber }}</span>
      </div>
      <div class="bank-item">
        <span class="bank-label">Branch</span>
        <span class="bank-value">{{ branchName }}</span>
      </div>
      <div class="bank-item">
        <span class="bank-label">Branch Code</span>
        <span class="bank-value">{{ branchCode }}</span>
      </div>
      <div v-if="swiftCode" class="bank-item">
        <span class="bank-label">Swift Code</span>
        <span class="bank-value">{{ swiftCode }}</span>
      </div>
    </div>

    <!-- Important Notice -->
    <div v-if="showNotice" class="bank-notice">
      <div class="notice-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      </div>
      <div class="notice-content">
        <slot name="notice">
          <strong>Important:</strong> Please use the payment reference provided when making your EFT payment.
          This ensures your payment is correctly applied to your account.
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BankDetails',

  props: {
    accountName: {
      type: String,
      default: 'Chrilan Technology Limited'
    },
    bankName: {
      type: String,
      default: 'FNB'
    },
    accountType: {
      type: String,
      default: 'Business Cheque Account'
    },
    accountNumber: {
      type: String,
      default: '62812689521'
    },
    branchName: {
      type: String,
      default: 'Commercial'
    },
    branchCode: {
      type: String,
      default: '260001'
    },
    swiftCode: {
      type: String,
      default: 'FIRNZMLX'
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showNotice: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Number,
      default: 2,
      validator: (value) => [1, 2].includes(value)
    }
  },

  computed: {
    gridClass() {
      return `bank-grid--cols-${this.columns}`
    }
  }
}
</script>

<style scoped>
.bank-details {
  animation: fadeIn 0.3s ease-out;
}

.bank-details--bordered {
  padding-top: var(--space-5, 1.25rem);
  margin-top: var(--space-5, 1.25rem);
  border-top: 1px solid var(--border-color, #e4e4e7);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Title */
.bank-title {
  font-size: var(--text-md, 0.875rem);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary, #18181b);
  margin: 0 0 var(--space-3, 0.75rem) 0;
}

/* Grid */
.bank-grid {
  display: grid;
  gap: var(--space-3, 0.75rem);
}

.bank-grid--cols-2 {
  grid-template-columns: repeat(2, 1fr);
}

.bank-grid--cols-1 {
  grid-template-columns: 1fr;
}

/* Items */
.bank-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Label */
.bank-label {
  font-size: var(--text-xs, 0.6875rem);
  color: var(--text-muted, #71717a);
  text-transform: uppercase;
  letter-spacing: 0.025em;
  font-weight: var(--font-medium, 500);
}

/* Value */
.bank-value {
  font-size: var(--text-base, 0.8125rem);
  font-weight: var(--font-medium, 500);
  color: var(--text-primary, #18181b);
}

.bank-value--highlight {
  color: var(--color-primary-500, #0ea5e9);
  font-family: var(--font-family-mono, monospace);
  font-size: var(--text-md, 0.875rem);
}

/* Notice */
.bank-notice {
  display: flex;
  gap: var(--space-3, 0.75rem);
  margin-top: var(--space-5, 1.25rem);
  padding: var(--space-4, 1rem);
  background: var(--color-warning-100, #fef3c7);
  border: 1px solid var(--color-warning-300, #fcd34d);
  border-radius: var(--radius-lg, 0.5rem);
}

.notice-icon {
  flex-shrink: 0;
  color: var(--color-warning-600, #d97706);
}

.notice-icon svg {
  width: 20px;
  height: 20px;
}

.notice-content {
  font-size: var(--text-base, 0.8125rem);
  color: var(--color-warning-800, #92400e);
  line-height: var(--leading-relaxed, 1.6);
}

/* Responsive */
@media (max-width: 576px) {
  .bank-grid--cols-2 {
    grid-template-columns: 1fr;
  }
}
</style>
